import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Import steps
import HotelSelectionStep from '@features/deals/components/WizardSteps/HotelSelectionStep';
import FinancialDataStep from '@features/deals/components/WizardSteps/FinancialDataStep';
import ReviewAnalyzeStep from '@features/deals/components/WizardSteps/ReviewAnalyzeStep';
import AiValuationStep from '@features/deals/components/WizardSteps/AiValuationStep';
import SaveDealStep from '@features/deals/components/WizardSteps/SaveDealStep';

// Import dialogs
  import LoginDialog from '@features/deals/components/WizardShared/LoginDialog';
import BackConfirmDialog from '@features/deals/components/WizardShared/BackConfirmDialog';

// Import the custom hook
import { useDealWizard } from '@features/deals/hooks/useDealWizard';

// Import constants
import { WIZARD_STEPS } from '@features/deals/components/constants';

// --- Helper Functions ---
const formatMetricValue = (value: string | number): string => {
  return typeof value === 'string' ? value : value.toString();
};

const NewDealWizard: React.FC = () => {
  // Use the custom hook to handle all the complex state and logic
  const {
    // State
    activeStep,
    hotelInfo,
    existingHotels,
    selectedHotelId,
    isNewHotel,
    metrics,
    aiInsights,
    loading,
    error,
    saveStatus,
    dealData,
    showBackConfirm,
    selectedCategory,
    newItemName,
    newItemYear,
    newItemAmount,
    success,
    ocrFinancialData,
    hotelAiAnalysis,
    showLoginDialog,
    editedData,
    deletedRows,

    // Setters
    setHotelInfo,
    setMetrics,
    setError,
    setSuccess,
    setDealData,
    setSelectedCategory,
    setNewItemName,
    setNewItemYear,
    setNewItemAmount,
    setShowLoginDialog,
    setEditedData,
    setDeletedRows,
    setShowBackConfirm,

    // Handlers
    handleHotelSelect,
    handleAddNewItem,
    handleNext,
    handleBackClick,
    handleBackConfirm,
    handleSaveDeal,
    handleLoginSuccess,
    handleSaveFinancials
  } = useDealWizard();

  // --- Render ---
  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <HotelSelectionStep
            hotelInfo={hotelInfo}
            setHotelInfo={setHotelInfo}
            existingHotels={existingHotels}
            selectedHotelId={selectedHotelId}
            isNewHotel={isNewHotel}
            handleHotelSelect={handleHotelSelect}
          />
        );
      case 1:
        return (
          <FinancialDataStep
            metrics={metrics}
            setMetrics={setMetrics}
            editedData={editedData}
            setEditedData={setEditedData}
            deletedRows={deletedRows}
            setDeletedRows={setDeletedRows}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            newItemName={newItemName}
            setNewItemName={setNewItemName}
            newItemYear={newItemYear}
            setNewItemYear={setNewItemYear}
            newItemAmount={newItemAmount}
            setNewItemAmount={setNewItemAmount}
            loading={loading}
            success={success}
            setSuccess={setSuccess}
            error={error}
            setError={setError}
            handleAddNewItem={handleAddNewItem}
          />
        );
      case 2:
        return (
          <ReviewAnalyzeStep
            hotelInfo={hotelInfo}
            metrics={metrics}
            hotelAiAnalysis={hotelAiAnalysis}
          />
        );
      case 3:
        return (
          <AiValuationStep
            loading={loading}
            error={error}
            aiInsights={aiInsights}
          />
        );
      case 4:
        return (
          <SaveDealStep
            dealData={dealData}
            setDealData={setDealData}
            aiInsights={aiInsights}
            saveStatus={saveStatus}
            error={error}
          />
        );
      default:
        return <Alert severity="error">Unknown step</Alert>;
    }
  };

  return (
    <Paper sx={{ p: 3, maxWidth: 1200, mx: 'auto', my: 4 }}>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {WIZARD_STEPS.map((label) => (
          <Step key={label}>
            <StepLabel>
              <Typography variant="button">{label}</Typography>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      <Box sx={{ minHeight: 400 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {renderStepContent(activeStep)}
      </Box>

      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="outlined" onClick={handleBackClick} disabled={activeStep === 0 || loading} startIcon={<ArrowBackIcon />}>
          Back
        </Button>

        <Button
          variant="contained"
          color={activeStep === WIZARD_STEPS.length - 1 ? 'success' : 'primary'}
          onClick={activeStep === WIZARD_STEPS.length - 1 ? handleSaveDeal : handleNext}
          disabled={
            loading ||
            (activeStep === WIZARD_STEPS.length - 1 && saveStatus === 'success') ||
            (activeStep === 0 && isNewHotel && !hotelInfo.name.trim()) ||
            (activeStep === 4 && (!dealData.name.trim() || !dealData.asking_price))
          }
          endIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {activeStep === WIZARD_STEPS.length - 1 ? 'Save Deal' : activeStep === WIZARD_STEPS.length - 2 ? 'Complete Analysis' : 'Next'}
        </Button>
      </Box>

      {/* Dialogs */}
      <BackConfirmDialog
        open={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={() => handleBackConfirm(true)}
        activeStep={activeStep}
      />

      <LoginDialog
        open={showLoginDialog}
        onClose={() => setShowLoginDialog(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </Paper>
  );
};

export default NewDealWizard;
